/**
 * API helpers for the Phase 8 admin screens (RBAC / tenant metadata / shared
 * datasets). All calls go through `apiJson`, which attaches the bearer token +
 * active-tenant header and returns a normalized { ok, status, data, error }.
 * Every endpoint here is re-authorized server-side (§4.7) — these helpers only
 * shape the request.
 */

import { apiJson } from './ApiClient';

// ── P4B: tenant-scoped RBAC (/api/admin/rbac) ──────────────────────────────

export const listTenantRoles = () => apiJson('/api/admin/rbac/roles');
export const getGrantablePermissions = () => apiJson('/api/admin/rbac/permissions');
export const createTenantRole = (name, permissions) =>
  apiJson('/api/admin/rbac/roles', { method: 'POST', body: { name, permissions } });
export const updateTenantRolePermissions = (roleId, permissions) =>
  apiJson(`/api/admin/rbac/roles/${roleId}/permissions`, { method: 'PUT', body: { permissions } });
export const deleteTenantRole = (roleId) =>
  apiJson(`/api/admin/rbac/roles/${roleId}`, { method: 'DELETE' });
export const setMembershipRoles = (membershipId, roleIds) =>
  apiJson(`/api/admin/rbac/memberships/${membershipId}/roles`, { method: 'PUT', body: { roleIds } });
export function listTenantMemberships() {
  return apiJson('/api/admin/rbac/memberships');
}
export const admitMember = (username) =>
  apiJson('/api/secure/memberships/admit', { method: 'POST', body: { username } });
export const deactivateMember = (membershipId) =>
  apiJson(`/api/admin/rbac/memberships/${membershipId}`, { method: 'DELETE' });
// Baseline-role toggle: promote USER→ADMIN / demote ADMIN→USER. Both return the
// member's updated role names; guard failures come back as 422 (LAST_ADMIN /
// SELF_LOCKOUT / SCOPE_VIOLATION) with the RbacException message in the body.
export const promoteMembership = (membershipId) =>
  apiJson(`/api/admin/rbac/memberships/${membershipId}/promote`, { method: 'POST' });
export const demoteMembership = (membershipId) =>
  apiJson(`/api/admin/rbac/memberships/${membershipId}/demote`, { method: 'POST' });

// ── P4B: platform RBAC (/api/root/rbac) ────────────────────────────────────

export const listPlatformPermissionCatalog = () => apiJson('/api/root/rbac/permissions');
export const listPlatformRoles = () => apiJson('/api/root/rbac/roles');
export const updateTemplatePermissions = (roleId, permissions) =>
  apiJson(`/api/root/rbac/templates/${roleId}/permissions`, { method: 'PUT', body: { permissions } });
export const grantTenantAdmin = (username, tenantId) =>
  apiJson(`/api/root/rbac/users/${encodeURIComponent(username)}/tenant-admin`, { method: 'POST', body: { tenantId } });
export const revokeTenantAdmin = (username, tenantId) =>
  apiJson(`/api/root/rbac/users/${encodeURIComponent(username)}/tenant-admin?tenantId=${tenantId}`, { method: 'DELETE' });
export const appointRoot = (username) =>
  apiJson(`/api/root/rbac/users/${encodeURIComponent(username)}/root`, { method: 'POST' });
export const revokeRoot = (username) =>
  apiJson(`/api/root/rbac/users/${encodeURIComponent(username)}/root`, { method: 'DELETE' });
export function listAllTenants() {
  return apiJson('/api/root/tenants');
}
// Cross-tenant "all users" listing (ROOT-only, gated PLATFORM_USER_MANAGE).
export function listAllPlatformUsers() {
  return apiJson('/api/root/rbac/users');
}

// ── P6B: tenant metadata (/api/admin/tenant-metadata) ──────────────────────

export const listTenantMetadata = () => apiJson('/api/admin/tenant-metadata/list');
export const getTenantMetadata = (id) => apiJson(`/api/admin/tenant-metadata/${id}`);
export const updateTenantMetadata = (id, descriptorJson, dictionaryJson) =>
  apiJson(`/api/admin/tenant-metadata/${id}`, { method: 'PUT', body: { descriptorJson, dictionaryJson } });
export const discoverTenantMetadata = () =>
  apiJson('/api/admin/tenant-metadata/discover', { method: 'POST' });
export const reconcileTenantMetadata = () =>
  apiJson('/api/admin/tenant-metadata/reconcile', { method: 'POST' });
export const previewTenantMetadata = (tableNames, source) =>
  apiJson('/api/admin/tenant-metadata/preview', { method: 'POST', body: { tableNames, source } });
export const commitTenantMetadata = (ids) =>
  apiJson('/api/admin/tenant-metadata/commit', { method: 'POST', body: { ids } });

// ── P7A: shared datasets (/api/root/datasets) ──────────────────────────────

export const listSharedDatasets = () => apiJson('/api/root/datasets');
export const listDatasetGrants = (datasetId) => apiJson(`/api/root/datasets/${datasetId}/grants`);
export const toggleDatasetGrant = (datasetId, tenantId, enabled) =>
  apiJson(`/api/root/datasets/${datasetId}/grants/${tenantId}`, { method: 'POST', body: { enabled } });
